import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Eye,
  Trash2,
  RefreshCw,
  Loader,
  Copy,
  CheckCircle,
  Pencil,
  Loader2,
  CheckCircle2,
  ExternalLink,
  Link2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Wallet,
  UserPlus,
  ArrowLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Mask } from "@/utils/mask";
import { PaymentAdvance, PaymentAdvanceResponse } from "@/types/paymentAdvance";
import {
  PAYMENT_ADVANCE_STATUS,
  PAYMENT_ADVANCE_STATUS_LABELS,
} from "@/utils/constants";
import {
  deletePaymentAdvance,
  checkPaymentAdvanceStatus,
  showPaymentAdvance,
  updatePaymentAdvanceClient,
  convertPaymentAdvanceToBalance,
} from "@/Api/paymentAdvances";
import { getClientsDataWithFilters, registerClientsData } from "@/Api/clients";
import { getSelectClientOrigins } from "@/Api/clientOrigins";
import { AsyncSearchableSelect } from "@/components/ui/async-searchable-select";
import { toast } from "sonner";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { If } from "@/components/If";
import { UpdateModal } from "../Update";
import QRCode from "react-qr-code";
import { useAuth } from "@/hooks/auth";
import { TypeUser } from "@/types/userType";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GENDERS } from "@/constants/gender";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

interface Client {
  id: number;
  uuid: string;
  name: string;
}

interface TableDataProps {
  dataApi: PaymentAdvanceResponse;
  pageActions: {
    next: () => void;
    previous: () => void;
    goToPage: (page: number) => void;
  };
  updateFront: React.Dispatch<React.SetStateAction<PaymentAdvanceResponse>>;
  onSort?: (column: string) => void;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
  onRefresh?: () => void;
}

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
  onSort,
  sortColumn,
  sortDirection,
  onRefresh: _onRefresh,
}: TableDataProps) => {
  const { user } = useAuth();
  const isSellerUser = user?.user_type_id === TypeUser.seller;
  const [checkingStatus, setCheckingStatus] = useState<{
    [key: string]: boolean;
  }>({});
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    item: PaymentAdvance | null;
  }>({
    isOpen: false,
    item: null,
  });
  const [viewModal, setViewModal] = useState<{
    isOpen: boolean;
    item: PaymentAdvance | null;
    loading: boolean;
  }>({
    isOpen: false,
    item: null,
    loading: false,
  });
  const [copiedTxid, setCopiedTxid] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    item: PaymentAdvance | null;
  }>({
    isOpen: false,
    item: null,
  });
  const [editClientModal, setEditClientModal] = useState<{
    isOpen: boolean;
    item: PaymentAdvance | null;
    selectedClient: Client | null;
    saving: boolean;
    showNewClientForm: boolean;
    newClientData: {
      name: string;
      phone: string;
      email: string;
      gender_id: string;
      client_origin_id: string;
    };
    savingNewClient: boolean;
  }>({
    isOpen: false,
    item: null,
    selectedClient: null,
    saving: false,
    showNewClientForm: false,
    newClientData: {
      name: "",
      phone: "",
      email: "",
      gender_id: "",
      client_origin_id: "",
    },
    savingNewClient: false,
  });
  const [clientOrigins, setClientOrigins] = useState<{ id: number; name: string }[]>([]);
  const [viewCheckingStatus, setViewCheckingStatus] = useState(false);
  const [convertingToBalance, setConvertingToBalance] = useState<{ [key: string]: boolean }>({});
  const [convertModal, setConvertModal] = useState<{
    isOpen: boolean;
    item: PaymentAdvance | null;
  }>({
    isOpen: false,
    item: null,
  });
  const viewIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (viewModal.isOpen && viewModal.item && viewModal.item.status === PAYMENT_ADVANCE_STATUS.PENDING) {
      const checkStatus = async () => {
        setViewCheckingStatus(true);
        try {
          const { data, ok } = await checkPaymentAdvanceStatus(viewModal.item!.uuid);
          if (ok && data?.response) {
            if (data.response.status === PAYMENT_ADVANCE_STATUS.PAID) {
              toast.success("Pagamento confirmado!");
              setViewModal((prev) => ({
                ...prev,
                item: prev.item
                  ? {
                      ...prev.item,
                      status: PAYMENT_ADVANCE_STATUS.PAID as PaymentAdvance["status"],
                      paid_at: data.response.paid_at,
                    }
                  : null,
              }));
              updateFront((prev) => ({
                ...prev,
                data: prev.data.map((p) =>
                  p.uuid === viewModal.item!.uuid
                    ? {
                        ...p,
                        status: PAYMENT_ADVANCE_STATUS.PAID as PaymentAdvance["status"],
                        paid_at: data.response.paid_at,
                      }
                    : p
                ),
              }));
              if (viewIntervalRef.current) {
                clearInterval(viewIntervalRef.current);
                viewIntervalRef.current = null;
              }
            }
          }
        } catch (error) {
          console.error("Error checking payment status:", error);
        } finally {
          setViewCheckingStatus(false);
        }
      };

      checkStatus();
      viewIntervalRef.current = setInterval(checkStatus, 10000);

      return () => {
        if (viewIntervalRef.current) {
          clearInterval(viewIntervalRef.current);
          viewIntervalRef.current = null;
        }
      };
    }
  }, [viewModal.isOpen, viewModal.item?.uuid, viewModal.item?.status]);

  const handleViewCheckStatus = async () => {
    if (!viewModal.item) return;
    setViewCheckingStatus(true);
    try {
      const { data, ok } = await checkPaymentAdvanceStatus(viewModal.item.uuid);
      if (ok && data?.response) {
        if (data.response.status === PAYMENT_ADVANCE_STATUS.PAID) {
          toast.success("Pagamento confirmado!");
          setViewModal((prev) => ({
            ...prev,
            item: prev.item
              ? {
                  ...prev.item,
                  status: PAYMENT_ADVANCE_STATUS.PAID as PaymentAdvance["status"],
                  paid_at: data.response.paid_at,
                }
              : null,
          }));
          updateFront((prev) => ({
            ...prev,
            data: prev.data.map((p) =>
              p.uuid === viewModal.item!.uuid
                ? {
                    ...p,
                    status: PAYMENT_ADVANCE_STATUS.PAID as PaymentAdvance["status"],
                    paid_at: data.response.paid_at,
                  }
                : p
            ),
          }));
        } else {
          toast.info("Pagamento ainda não confirmado");
        }
      }
    } catch (error) {
      toast.error("Erro ao verificar status");
    } finally {
      setViewCheckingStatus(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case PAYMENT_ADVANCE_STATUS.PENDING:
        return "bg-yellow-500 text-white border-yellow-500";
      case PAYMENT_ADVANCE_STATUS.PAID:
        return "bg-green-500 text-white border-green-500";
      case PAYMENT_ADVANCE_STATUS.USED:
        return "bg-blue-500 text-white border-blue-500";
      case PAYMENT_ADVANCE_STATUS.CANCELLED:
        return "bg-red-500 text-white border-red-500";
      case PAYMENT_ADVANCE_STATUS.EXPIRED:
        return "bg-gray-500 text-white border-gray-500";
      case PAYMENT_ADVANCE_STATUS.CONVERTED:
        return "bg-purple-500 text-white border-purple-500";
      default:
        return "bg-gray-500 text-white border-gray-500";
    }
  };

  const getRowColor = (item: PaymentAdvance): string => {
    if (item.status === PAYMENT_ADVANCE_STATUS.CONVERTED) {
      return "bg-purple-50 hover:bg-purple-100 border-l-4 border-purple-500";
    }
    if (item.sale_id) {
      return "bg-green-50 hover:bg-green-100 border-l-4 border-green-500";
    }
    if (item.status === PAYMENT_ADVANCE_STATUS.PAID) {
      return "bg-blue-50 hover:bg-blue-100 border-l-4 border-blue-500";
    }
    return "hover:bg-zinc-100 dark:hover:bg-zinc-800";
  };

  const handleCheckStatus = async (item: PaymentAdvance) => {
    setCheckingStatus((prev) => ({ ...prev, [item.uuid]: true }));
    try {
      const { data, ok } = await checkPaymentAdvanceStatus(item.uuid);
      if (ok && data?.response) {
        toast.success(data.response.message);
        if (data.response.status === PAYMENT_ADVANCE_STATUS.PAID) {
          updateFront((prev) => ({
            ...prev,
            data: prev.data.map((p) =>
              p.uuid === item.uuid
                ? {
                    ...p,
                    status:
                      PAYMENT_ADVANCE_STATUS.PAID as PaymentAdvance["status"],
                    paid_at: data.response.paid_at,
                  }
                : p
            ),
          }));
        }
      }
    } catch (error) {
      toast.error("Erro ao verificar status do pagamento");
    } finally {
      setCheckingStatus((prev) => ({ ...prev, [item.uuid]: false }));
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.item) return;

    try {
      const { ok } = await deletePaymentAdvance(deleteModal.item.uuid);
      if (ok) {
        toast.success("Pix Gerado excluído com sucesso!");
        updateFront((prev) => ({
          ...prev,
          data: prev.data.filter((p) => p.uuid !== deleteModal.item?.uuid),
        }));
      }
    } catch (error) {
      toast.error("Erro ao excluir Pix Gerado");
    } finally {
      setDeleteModal({ isOpen: false, item: null });
    }
  };

  const handleView = async (item: PaymentAdvance) => {
    setViewModal({ isOpen: true, item: null, loading: true });
    try {
      const { data, ok } = await showPaymentAdvance(item.uuid);
      if (ok && data?.response) {
        setViewModal({ isOpen: true, item: data.response, loading: false });
      } else {
        setViewModal({ isOpen: false, item: null, loading: false });
        toast.error("Erro ao carregar detalhes");
      }
    } catch (error) {
      setViewModal({ isOpen: false, item: null, loading: false });
      toast.error("Erro ao carregar detalhes");
    }
  };

  const handleCopyQrCode = (qrcode: string, txid: string) => {
    navigator.clipboard.writeText(qrcode);
    setCopiedTxid(txid);
    toast.success("Código PIX copiado!");
    setTimeout(() => setCopiedTxid(null), 2000);
  };

  const getPublicLink = (uuid: string) => {
    return `${window.location.origin}/public/pix-gerado/${uuid}`;
  };

  const handleCopyPublicLink = (uuid: string) => {
    const link = getPublicLink(uuid);
    navigator.clipboard.writeText(link);
    setCopiedLink(uuid);
    toast.success("Link público copiado!");
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const handleOpenPublicLink = (uuid: string) => {
    const link = getPublicLink(uuid);
    window.open(link, "_blank");
  };

  const canEdit = (item: PaymentAdvance): boolean => {
    return !isSellerUser && item.status === PAYMENT_ADVANCE_STATUS.PENDING;
  };

  const canEditClient = (item: PaymentAdvance): boolean => {
    return !isSellerUser && item.status === PAYMENT_ADVANCE_STATUS.PAID && !item.sale_id;
  };

  const canDelete = (item: PaymentAdvance): boolean => {
    return !isSellerUser && item.status === PAYMENT_ADVANCE_STATUS.PENDING;
  };

  const canConvertToBalance = (item: PaymentAdvance): boolean => {
    return !isSellerUser && item.status === PAYMENT_ADVANCE_STATUS.PAID && !item.sale_id;
  };

  const handleConvertToBalance = async () => {
    if (!convertModal.item) return;

    setConvertingToBalance((prev) => ({ ...prev, [convertModal.item!.uuid]: true }));
    try {
      const { data, ok } = await convertPaymentAdvanceToBalance(convertModal.item.uuid);
      if (ok && data?.response) {
        toast.success("Pagamento convertido em saldo com sucesso!");
        updateFront((prev) => ({
          ...prev,
          data: prev.data.map((p) =>
            p.uuid === convertModal.item!.uuid
              ? {
                  ...p,
                  status: PAYMENT_ADVANCE_STATUS.CONVERTED as PaymentAdvance["status"],
                }
              : p
          ),
        }));
      } else {
        toast.error(data?.message || "Erro ao converter pagamento em saldo");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao converter pagamento em saldo");
    } finally {
      setConvertingToBalance((prev) => ({ ...prev, [convertModal.item!.uuid]: false }));
      setConvertModal({ isOpen: false, item: null });
    }
  };

  const searchClients = useCallback(async (searchTerm: string): Promise<Client[]> => {
    try {
      const { data, ok } = await getClientsDataWithFilters({
        name: searchTerm,
        per_page: 20,
        is_active: true,
      });
      if (ok && data?.response?.data) {
        return data.response.data;
      }
      return [];
    } catch (error) {
      console.error("Error searching clients:", error);
      return [];
    }
  }, []);

  const handleSaveClient = async () => {
    if (!editClientModal.item || !editClientModal.selectedClient) return;

    setEditClientModal((prev) => ({ ...prev, saving: true }));
    try {
      const { ok } = await updatePaymentAdvanceClient(
        editClientModal.item.uuid,
        editClientModal.selectedClient.id
      );
      if (ok) {
        toast.success("Cliente atualizado com sucesso!");
        updateFront((prev) => ({
          ...prev,
          data: prev.data.map((p) =>
            p.uuid === editClientModal.item!.uuid
              ? {
                  ...p,
                  client_id: editClientModal.selectedClient!.id,
                  client: p.client
                    ? {
                        ...p.client,
                        id: editClientModal.selectedClient!.id,
                        name: editClientModal.selectedClient!.name,
                      }
                    : {
                        id: editClientModal.selectedClient!.id,
                        uuid: editClientModal.selectedClient!.uuid,
                        name: editClientModal.selectedClient!.name,
                        email: "",
                        phone: "",
                      },
                }
              : p
          ),
        }));
        setEditClientModal({ isOpen: false, item: null, selectedClient: null, saving: false, showNewClientForm: false, newClientData: { name: "", phone: "", email: "", gender_id: "", client_origin_id: "" }, savingNewClient: false });
      } else {
        toast.error("Erro ao atualizar cliente");
        setEditClientModal((prev) => ({ ...prev, saving: false }));
      }
    } catch (error) {
      toast.error("Erro ao atualizar cliente");
      setEditClientModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const fetchClientOrigins = useCallback(async () => {
    try {
      const { data, ok } = await getSelectClientOrigins();
      if (ok && data?.response) {
        setClientOrigins(data.response);
      }
    } catch (error) {
      console.error("Error fetching client origins:", error);
    }
  }, []);

  const handleShowNewClientForm = () => {
    fetchClientOrigins();
    setEditClientModal((prev) => ({ ...prev, showNewClientForm: true }));
  };

  const handleSaveNewClient = async () => {
    const { newClientData, item } = editClientModal;

    if (!newClientData.name || !newClientData.phone || !newClientData.gender_id || !newClientData.client_origin_id) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    setEditClientModal((prev) => ({ ...prev, savingNewClient: true }));

    try {
      const formData = new FormData();
      formData.append("name", newClientData.name);
      formData.append("phone", newClientData.phone);
      formData.append("email", newClientData.email || "");
      formData.append("gender_id", newClientData.gender_id);
      formData.append("client_origin_id", newClientData.client_origin_id);

      const { data: apiResponse, ok } = await registerClientsData(formData);

      if (ok && apiResponse?.response) {
        toast.success("Cliente cadastrado com sucesso!");
        const newClient = apiResponse.response;

        setEditClientModal((prev) => ({
          ...prev,
          showNewClientForm: false,
          selectedClient: { id: newClient.id, uuid: newClient.uuid, name: newClient.name },
          newClientData: { name: "", phone: "", email: "", gender_id: "", client_origin_id: "" },
          savingNewClient: false,
        }));

        if (item) {
          const { ok: updateOk } = await updatePaymentAdvanceClient(item.uuid, newClient.id);
          if (updateOk) {
            updateFront((prev) => ({
              ...prev,
              data: prev.data.map((p) =>
                p.uuid === item.uuid
                  ? {
                      ...p,
                      client_id: newClient.id,
                      client: {
                        id: newClient.id,
                        uuid: newClient.uuid,
                        name: newClient.name,
                        email: newClientData.email || "",
                        phone: newClientData.phone,
                      },
                    }
                  : p
              ),
            }));
            toast.success("Cliente vinculado ao PIX com sucesso!");
            setEditClientModal({ isOpen: false, item: null, selectedClient: null, saving: false, showNewClientForm: false, newClientData: { name: "", phone: "", email: "", gender_id: "", client_origin_id: "" }, savingNewClient: false });
          }
        }
      } else {
        toast.error(apiResponse?.message || "Erro ao cadastrar cliente");
        setEditClientModal((prev) => ({ ...prev, savingNewClient: false }));
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao cadastrar cliente");
      setEditClientModal((prev) => ({ ...prev, savingNewClient: false }));
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="ml-1 h-4 w-4 inline-block opacity-50" />;
    }
    return sortDirection === "asc"
      ? <ArrowUp className="ml-1 h-4 w-4 inline-block" />
      : <ArrowDown className="ml-1 h-4 w-4 inline-block" />;
  };

  const SortableHeader = ({ column, children, className }: { column: string; children: React.ReactNode; className?: string }) => (
    <TableHead
      className={cn(
        "text-white font-bold cursor-pointer hover:bg-blue-700 select-none transition-colors",
        className
      )}
      onClick={() => onSort?.(column)}
    >
      {children}
      {getSortIcon(column)}
    </TableHead>
  );

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow className="bg-blue-600 hover:bg-blue-600">
            <SortableHeader column="id">ID</SortableHeader>
            <SortableHeader column="client_name">Cliente</SortableHeader>
            <SortableHeader column="seller_name">Vendedor</SortableHeader>
            <SortableHeader column="amount" className="text-right">Valor</SortableHeader>
            <TableHead className="text-white font-bold text-center">Pago</TableHead>
            <TableHead className="text-white font-bold text-center">Linkado a Venda</TableHead>
            <SortableHeader column="created_at">Data de Criação</SortableHeader>
            <TableHead className="text-white font-bold text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.length > 0 ? (
            dataApi.data.map((item) => (
              <TableRow
                key={item.uuid}
                className={cn(
                  "cursor-pointer transition-colors",
                  getRowColor(item)
                )}
              >
                <TableCell className="font-medium">#{item.id}</TableCell>
                <TableCell className="max-w-[150px] truncate">
                  {item.client?.name || "N/A"}
                </TableCell>
                <TableCell className="max-w-[120px] truncate">
                  {item.seller?.user?.name || "N/A"}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {Mask.formatCurrency(item.amount)}
                </TableCell>
                <TableCell className="text-center">
                  <Badge
                    variant="outline"
                    className={
                      item.status === PAYMENT_ADVANCE_STATUS.PAID ||
                      item.status === PAYMENT_ADVANCE_STATUS.USED ||
                      item.status === PAYMENT_ADVANCE_STATUS.CONVERTED
                        ? "bg-green-100 text-green-700 border-green-500"
                        : "bg-red-100 text-red-700 border-red-500"
                    }
                  >
                    {item.status === PAYMENT_ADVANCE_STATUS.PAID ||
                    item.status === PAYMENT_ADVANCE_STATUS.USED ||
                    item.status === PAYMENT_ADVANCE_STATUS.CONVERTED
                      ? "SIM"
                      : "NÃO"}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  {item.status === PAYMENT_ADVANCE_STATUS.CONVERTED ? (
                    <Badge
                      variant="outline"
                      className="bg-purple-100 text-purple-700 border-purple-500"
                    >
                      Convertido para Saldo
                    </Badge>
                  ) : item.sale_id ? (
                    <Badge
                      variant="outline"
                      className="bg-blue-100 text-blue-700 border-blue-500"
                    >
                      ID #{item.sale_id}
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                    >
                      Não Linkado
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {item.created_at
                    ? new Date(item.created_at).toLocaleString("pt-BR")
                    : "N/A"}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                      onClick={() => handleView(item)}
                      title="Visualizar"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                      onClick={() => handleCopyPublicLink(item.uuid)}
                      title="Copiar Link Público"
                    >
                      {copiedLink === item.uuid ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Link2 className="h-4 w-4" />
                      )}
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                      onClick={() => handleOpenPublicLink(item.uuid)}
                      title="Abrir Link Público"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>

                    {item.status === PAYMENT_ADVANCE_STATUS.PENDING && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                        onClick={() => handleCheckStatus(item)}
                        disabled={checkingStatus[item.uuid]}
                        title="Verificar Status"
                      >
                        {checkingStatus[item.uuid] ? (
                          <Loader className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    )}

                    <If condition={canEdit(item)}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                        title="Editar"
                        onClick={() => setEditModal({ isOpen: true, item })}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </If>

                    <If condition={canEditClient(item)}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                        title="Editar Cliente"
                        onClick={() => setEditClientModal((prev) => ({
                          ...prev,
                          isOpen: true,
                          item,
                          selectedClient: item.client ? { id: item.client.id, uuid: "", name: item.client.name } : null,
                          saving: false,
                        }))}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </If>

                    <If condition={canConvertToBalance(item)}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 p-0 text-purple-600 hover:bg-purple-100 dark:text-purple-400 dark:hover:bg-purple-900/30"
                        title="Converter para Saldo"
                        onClick={() => setConvertModal({ isOpen: true, item })}
                        disabled={convertingToBalance[item.uuid]}
                      >
                        {convertingToBalance[item.uuid] ? (
                          <Loader className="h-4 w-4 animate-spin" />
                        ) : (
                          <Wallet className="h-4 w-4" />
                        )}
                      </Button>
                    </If>

                    <If condition={canDelete(item)}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                        onClick={() => setDeleteModal({ isOpen: true, item })}
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </If>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-8">
                Nenhum Pix Gerado encontrado.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-between items-center mt-4">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium text-sm">
            Exibindo {dataApi?.current_page} de {dataApi?.last_page} páginas
            (Total: {dataApi?.total})
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn(
                    "h-8",
                    dataApi?.current_page === 1
                      ? "opacity-50 pointer-events-none"
                      : colors.bg_primary
                  )}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              {Array.from(
                { length: Math.min(5, dataApi.last_page) },
                (_, i) => {
                  const page = i + 1;
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => pageActions.goToPage(page)}
                        isActive={page === dataApi.current_page}
                        className={`cursor-pointer ${
                          page !== dataApi.current_page
                            ? "hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100"
                            : ""
                        }`}
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                }
              )}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn(
                    "h-8",
                    dataApi?.current_page === dataApi?.last_page
                      ? "opacity-50 pointer-events-none"
                      : colors.bg_primary
                  )}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      {/* View Modal */}
      <Dialog
        open={viewModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            if (viewIntervalRef.current) {
              clearInterval(viewIntervalRef.current);
              viewIntervalRef.current = null;
            }
            setViewModal({ isOpen: false, item: null, loading: false });
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Detalhes do Pix Gerado</DialogTitle>
          </DialogHeader>
          {viewModal.loading ? (
            <div className="flex justify-center py-8">
              <Loader className="h-8 w-8 animate-spin" />
            </div>
          ) : viewModal.item ? (
            <div className="space-y-4">
              {viewModal.item.status === PAYMENT_ADVANCE_STATUS.PAID ||
              viewModal.item.status === PAYMENT_ADVANCE_STATUS.USED ? (
                <div className="flex flex-col items-center justify-center py-4">
                  <div className="bg-green-100 p-4 rounded-full mb-4">
                    <CheckCircle2 className="w-12 h-12 text-green-600" />
                  </div>
                  <h4 className="text-xl font-bold text-green-600 mb-2">
                    Pagamento Confirmado!
                  </h4>
                  <p className="text-2xl font-bold text-green-800">
                    {Mask.formatCurrency(viewModal.item.amount)}
                  </p>
                </div>
              ) : viewModal.item.pix_qrcode ? (
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-lg mb-4 border">
                    <QRCode
                      value={viewModal.item.pix_qrcode}
                      size={192}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    />
                  </div>
                  <p className="text-2xl font-bold text-green-600 mb-4">
                    {Mask.formatCurrency(viewModal.item.amount)}
                  </p>
                  <div className="w-full max-w-sm">
                    <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-md mb-4">
                      <p className="text-xs flex-1 truncate">
                        {viewModal.item.pix_qrcode}
                      </p>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() =>
                          handleCopyQrCode(
                            viewModal.item!.pix_qrcode!,
                            viewModal.item!.pix_txid!
                          )
                        }
                        className="h-8 w-8 shrink-0"
                      >
                        {copiedTxid === viewModal.item.pix_txid ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleViewCheckStatus}
                      disabled={viewCheckingStatus}
                    >
                      {viewCheckingStatus ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verificando...
                        </>
                      ) : (
                        "Verificar pagamento"
                      )}
                    </Button>
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleCopyPublicLink(viewModal.item!.uuid)}
                      >
                        {copiedLink === viewModal.item.uuid ? (
                          <>
                            <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                            Link Copiado!
                          </>
                        ) : (
                          <>
                            <Link2 className="mr-2 h-4 w-4" />
                            Copiar Link
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleOpenPublicLink(viewModal.item!.uuid)}
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Abrir Link
                      </Button>
                    </div>
                    <p className="text-xs text-center mt-2 text-muted-foreground">
                      O status do pagamento é verificado automaticamente a cada 10 segundos.
                    </p>
                  </div>
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    ID
                  </label>
                  <p className="text-lg font-semibold">#{viewModal.item.id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Status
                  </label>
                  <Badge
                    variant="outline"
                    className={getStatusColor(viewModal.item.status)}
                  >
                    {PAYMENT_ADVANCE_STATUS_LABELS[viewModal.item.status]}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Cliente
                  </label>
                  <p>{viewModal.item.client?.name || "N/A"}</p>
                </div>
                {viewModal.item.payer_name && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Pagador
                    </label>
                    <p>{viewModal.item.payer_name}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Vendedor
                  </label>
                  <p>{viewModal.item.seller?.user?.name || "N/A"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Linkado à Venda
                  </label>
                  <p>
                    {viewModal.item.sale_id
                      ? `#${viewModal.item.sale_id}`
                      : "Não linkado"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Data de Criação
                  </label>
                  <p>
                    {viewModal.item.created_at
                      ? new Date(viewModal.item.created_at).toLocaleString(
                          "pt-BR"
                        )
                      : "N/A"}
                  </p>
                </div>
                {viewModal.item.paid_at && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Data do Pagamento
                    </label>
                    <p>
                      {new Date(viewModal.item.paid_at).toLocaleString("pt-BR")}
                    </p>
                  </div>
                )}
              </div>

              {viewModal.item.observation && (
                <div className="pt-4 border-t">
                  <label className="text-sm font-medium text-gray-500">
                    Observação
                  </label>
                  <p className="text-gray-700">{viewModal.item.observation}</p>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <AlertDialog
        open={deleteModal.isOpen}
        onOpenChange={(open) =>
          !open && setDeleteModal({ isOpen: false, item: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este Pix Gerado? Esta ação não pode
              ser desfeita.
              {deleteModal.item && (
                <span className="block mt-2 font-medium">
                  ID: #{deleteModal.item.id} - Valor:{" "}
                  {Mask.formatCurrency(deleteModal.item.amount)}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Modal */}
      <UpdateModal
        isOpen={editModal.isOpen}
        onClose={() => setEditModal({ isOpen: false, item: null })}
        item={editModal.item}
        updateFront={updateFront}
      />

      {/* Edit Client Modal */}
      <Dialog
        open={editClientModal.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditClientModal({ isOpen: false, item: null, selectedClient: null, saving: false, showNewClientForm: false, newClientData: { name: "", phone: "", email: "", gender_id: "", client_origin_id: "" }, savingNewClient: false });
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editClientModal.showNewClientForm ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setEditClientModal((prev) => ({ ...prev, showNewClientForm: false }))}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  Cadastrar Novo Cliente
                </div>
              ) : (
                "Editar Cliente do Pix"
              )}
            </DialogTitle>
          </DialogHeader>

          {editClientModal.showNewClientForm ? (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pix ID</Label>
                  <p className="text-lg font-semibold">#{editClientModal.item?.id}</p>
                </div>
                <div className="space-y-2">
                  <Label>Valor</Label>
                  <p className="text-lg font-semibold text-green-600">
                    {editClientModal.item ? Mask.formatCurrency(editClientModal.item.amount) : ""}
                  </p>
                </div>
              </div>

              <div className="border-t pt-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-client-name">Nome *</Label>
                  <Input
                    id="new-client-name"
                    value={editClientModal.newClientData.name}
                    onChange={(e) => setEditClientModal((prev) => ({
                      ...prev,
                      newClientData: { ...prev.newClientData, name: e.target.value }
                    }))}
                    placeholder="Nome do cliente"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-client-phone">Telefone *</Label>
                  <PhoneInput
                    id="new-client-phone"
                    defaultCountry="BR"
                    value={editClientModal.newClientData.phone}
                    onChange={(value) => setEditClientModal((prev) => ({
                      ...prev,
                      newClientData: { ...prev.newClientData, phone: value || "" }
                    }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-client-email">E-mail</Label>
                  <Input
                    id="new-client-email"
                    type="email"
                    value={editClientModal.newClientData.email}
                    onChange={(e) => setEditClientModal((prev) => ({
                      ...prev,
                      newClientData: { ...prev.newClientData, email: e.target.value }
                    }))}
                    placeholder="E-mail do cliente (opcional)"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sexo *</Label>
                    <Select
                      value={editClientModal.newClientData.gender_id}
                      onValueChange={(value) => setEditClientModal((prev) => ({
                        ...prev,
                        newClientData: { ...prev.newClientData, gender_id: value }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDERS.map((gender) => (
                          <SelectItem key={gender.id} value={gender.id.toString()}>
                            {gender.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Origem *</Label>
                    <Select
                      value={editClientModal.newClientData.client_origin_id}
                      onValueChange={(value) => setEditClientModal((prev) => ({
                        ...prev,
                        newClientData: { ...prev.newClientData, client_origin_id: value }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientOrigins.map((origin) => (
                          <SelectItem key={origin.id} value={origin.id.toString()}>
                            {origin.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setEditClientModal((prev) => ({ ...prev, showNewClientForm: false }))}
                  disabled={editClientModal.savingNewClient}
                >
                  Voltar
                </Button>
                <Button
                  onClick={handleSaveNewClient}
                  disabled={editClientModal.savingNewClient}
                  className={cn(colors.bg_primary)}
                >
                  {editClientModal.savingNewClient ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Cadastrando...
                    </>
                  ) : (
                    "Cadastrar e Vincular"
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Pix ID</label>
                  <p className="text-lg font-semibold">#{editClientModal.item?.id}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Valor</label>
                  <p className="text-lg font-semibold text-green-600">
                    {editClientModal.item ? Mask.formatCurrency(editClientModal.item.amount) : ""}
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Cliente</label>
                  <AsyncSearchableSelect<Client>
                    fetchItems={searchClients}
                    displayField="name"
                    placeholder="Buscar cliente..."
                    searchPlaceholder="Digite o nome..."
                    emptyMessage="Nenhum cliente encontrado."
                    value={editClientModal.selectedClient}
                    onSelect={(client) => setEditClientModal((prev) => ({ ...prev, selectedClient: client }))}
                    getItemId={(item) => item.id}
                  />
                </div>

                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={handleShowNewClientForm}
                    className="text-green-600 border-green-600 hover:bg-green-50"
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    Cadastrar Novo Cliente
                  </Button>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setEditClientModal({ isOpen: false, item: null, selectedClient: null, saving: false, showNewClientForm: false, newClientData: { name: "", phone: "", email: "", gender_id: "", client_origin_id: "" }, savingNewClient: false })}
                  disabled={editClientModal.saving}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveClient}
                  disabled={!editClientModal.selectedClient || editClientModal.saving}
                  className={cn(colors.bg_primary)}
                >
                  {editClientModal.saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    "Salvar"
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Convert to Balance Confirmation Modal */}
      <AlertDialog
        open={convertModal.isOpen}
        onOpenChange={(open) =>
          !open && setConvertModal({ isOpen: false, item: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Converter para Saldo do Cliente</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja converter este pagamento PIX em saldo na carteira do cliente?
              {convertModal.item && (
                <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Pix ID:</span>
                    <span className="font-semibold">#{convertModal.item.id}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-600 dark:text-gray-400">Valor:</span>
                    <span className="font-bold text-purple-600 dark:text-purple-400 text-lg">
                      {Mask.formatCurrency(convertModal.item.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-gray-600 dark:text-gray-400">Cliente:</span>
                    <span className="font-semibold">{convertModal.item.client?.name || "N/A"}</span>
                  </div>
                </div>
              )}
              <p className="mt-4 text-sm text-amber-600 dark:text-amber-400">
                Esta ação não pode ser desfeita. O pagamento será marcado como "Convertido" e o valor será adicionado ao saldo do cliente.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={convertModal.item ? convertingToBalance[convertModal.item.uuid] : false}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConvertToBalance}
              className="bg-purple-600 hover:bg-purple-700"
              disabled={convertModal.item ? convertingToBalance[convertModal.item.uuid] : false}
            >
              {convertModal.item && convertingToBalance[convertModal.item.uuid] ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Convertendo...
                </>
              ) : (
                <>
                  <Wallet className="mr-2 h-4 w-4" />
                  Converter para Saldo
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
